import { toast } from 'react-toastify'
import { getContrastRatio, invertTextColor, getReadableTextColor } from './contrast'
import Api from './api'

function vcfText({
  prefix,
  firstName,
  lastName,
  organisationName,
  email,
  phone,
  phones,
  jobTitle,
  photo,
  companyURL,
  linkedInURL,
  socials,
  addresses,
  personalAccount,
}) {
  const getFullName = () => {
    if (prefix) return prefix + ' ' + firstName + ' ' + lastName
    return firstName + ' ' + lastName
  }

  const getName = () => {
    if (prefix) return `${lastName};${firstName};;${prefix};`
    return `${lastName};${firstName};;;`
  }

  const filterArray = () => {
    const seenValues = {}

    for (const item of phones) {
      const value = item.value

      if (!seenValues[value]) {
        seenValues[value] = true
        vcfPhones.push(item)
      }
    }
  }

  let vcfPhones = []
  if (phones) filterArray()
  if (
    phone &&
    !vcfPhones.find((item) => item.value == phone) &&
    !phone.includes('undefined')
  )
    vcfPhones.push({ label: 'WORK', value: phone })

  let str = `BEGIN:VCARD
VERSION:3.0
ORG:${organisationName}
FN:${getFullName()}
N:${getName()}
TITLE:${jobTitle}
EMAIL;TYPE=:${email}
PHOTO;VALUE=URL;TYPE=JPEG:${photo}
URL:${companyURL || ''}`

  if (personalAccount)
    str = str + `\nURL;TYPE=Saved via Cardly:https://www.cardlysa.com`

  if (linkedInURL) str = str + `\nX-SOCIALPROFILE;TYPE=linkedin:${linkedInURL}`

  if (socials)
    socials.forEach(({ type, value }) => {
      str = str + `\nX-SOCIALPROFILE;TYPE=${type || 'social'}:${value}`
    })

  if (addresses)
    addresses.forEach(({ type, value }) => {
      str = str + `\nADR;TYPE=${type || 'WORK'}:;;${value}`
    })

  // if (phone) str = str + `\nTEL:${phone}`;

  if (vcfPhones)
    vcfPhones.forEach(({ type, value }) => {
      if (value.includes('undefined')) return
      str = str + `\nTEL;TYPE=${type || 'WORK'}:${value}`
    })

  str = str + `\nEND:VCARD`

  return str
}
const showToastMessage = (message) => {
  toast(message, {
    position: toast.POSITION.TOP_CENTER,
  })
}

function convert({ file, width, height, type }) {
  return new Promise((resolve, reject) => {
    const allow = ['jpg', 'gif', 'bmp', 'png', 'jpeg', 'svg']
    try {
      if (
        file.name &&
        file.name.split('.').reverse()[0] &&
        allow.includes(file.name.split('.').reverse()[0].toLowerCase()) &&
        file.size &&
        file.type
      ) {
        const imageType = type || 'jpeg'
        const imgWidth = width || 500
        const imgHeight = height || 300
        const fileName = file.name
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = (event) => {
          const img = new Image()
          img.src = event.target.result
          img.onload = () => {
            const elem = document.createElement('canvas')
            elem.width = imgWidth
            elem.height = imgHeight
            const ctx = elem.getContext('2d')
            ctx.globalCompositeOperation = 'destination-over'
            ctx.drawImage(img, 0, 0, imgWidth, imgHeight)
            ctx.canvas.toBlob(
              (blob) => {
                const file = new File([blob], fileName, {
                  type: `image/${imageType.toLowerCase()}`,
                  lastModified: Date.now(),
                })
                resolve(file)
              },
              `image/${type || 'jpeg'}`,
              1
            )
          }
          reader.onerror = (error) => reject(error)
        }
        // eslint-disable-next-line prefer-promise-reject-errors
      } else reject('File not supported!')
    } catch (error) {
      console.log('Error while image resize: ', error)
      reject(error)
    }
  })
}

const resizeImage = async ({ file, width, height }) =>
  convert({ file, width, height, type: 'png' })

const formateDate = (date) => {
  return new Date(date).toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

const formateTime = (date) => {
  return new Date(date).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

const isEmptyJSON = (json) => JSON.stringify(json) === '{}'

const CurrencyFormat = ({ amount = 0, currency }) => {
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD', // Change this to your desired currency code
    minimumFractionDigits: 2,
  })
  return currencyFormatter.format(amount)
}

const UploadImage = ({ notify, file, desiredPath }) => {
  const form = new FormData()
  form.append('image', file, file.name)
  return Api('/utils/upload/image?desiredPath=' + desiredPath, 'post', {
    payload: form,
    notify,
  })
}

const getImageURL = (img) => {
  return typeof img == 'object' ? URL.createObjectURL(img) : img
}

// Change from the earlier figure to the later one, e.g. "+12%". It used to divide by the
// later figure (NaN% / -100% when values were zero); '—' when there is no earlier figure.
const getPercent = ({ newNum = 0, oldNum = 0 }) => {
  const before = Number(oldNum) || 0
  const now = Number(newNum) || 0
  if (!before) return now ? '—' : '0%'
  const percentage = Math.round(((now - before) / before) * 100)
  return percentage > 0 ? `+${percentage}%` : `${percentage}%`
}

// Direction follows the first strong character so mixed English/Arabic labels
// do not inherit the direction of the surrounding page. Codes, URLs and
// numbers remain LTR because they contain no Arabic strong character.
const getTextDirection = (value) => {
  const text = String(value || '')
  return /[\u0600-\u06ff\u0750-\u077f\u08a0-\u08ff]/.test(text)
    ? 'rtl'
    : 'ltr'
}


// The public opt-in URL a customer lands on. Mirrors the precedence the server
// uses in GetOptinURL: an agency's own growthtool domain wins, otherwise the
// current host, rewritten from the admin app to the public rewards domain.
const getOptinURL = ({ agency, campaign }) => {
  const growthtool_code = campaign?.growthtool?.growthtool_code
  const campaign_code = campaign?.campaign_code

  // a campaign that has not been saved yet has no codes, so there is no
  // landing page to point at
  if (!growthtool_code || !campaign_code) return ''

  const domain = agency?.growthtool_config?.domains?.[0]
  const origin = domain
    ? 'https://' + domain
    : window.location.origin.replace(
        'app.walletcampaigns.com',
        'rewards.addingtowallet.com'
      )

  // studio campaigns have their own join page (TASK-067)
  if (campaign.studio) return origin + '/join/' + growthtool_code
  return origin + '/registration/tool/' + growthtool_code + '/' + campaign_code
}

export {
  vcfText,
  getOptinURL,
  invertTextColor,
  getContrastRatio,
  getReadableTextColor,
  getTextDirection,
  showToastMessage,
  isEmptyJSON,
  formateDate,
  formateTime,
  CurrencyFormat,
  UploadImage,
  getImageURL,
  getPercent,
  resizeImage,
}
